import React from 'react';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import { useMedicalProfileDirectives } from '../../../../../../hooks/api/useMedicalProfileDirectives';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import { getIntl } from '../../../../../../utils/intl';
import { useHasPermission } from '../../../../../v2BaseComponents/Permissions/hooks';
import Button from '../../../../../v2BaseComponents/Button';
import { View } from 'react-native';
import { useMaybeRenderedSideDrawerToggle } from '../../../../../v2BaseComponents/SideDrawer/MaybeRenderedSideDrawer';
import EditCodeStatusSideDrawerContent from './EditCodeStatusSideDrawerContent';
import { APITypes } from '../../../../../../primary-record-types';
import Stack, { contentStackSpacing } from '../../../../../v2BaseComponents/Stack';
import Text from '../../../../../v2BaseComponents/Text';
import { codeStatusTypeMessages } from '../../../../../../utils/sharedMessages';
import Divider from '../../../../../v2BaseComponents/Divider';
import { useCodeStatusDescriptiveLabel } from '../../../../../../hooks/useCodeStatusDescriptiveLabel';
import FileCard from '../../../../../shared/FileCard';

const intl = getIntl('CodeStatusSection', {
    selectCodeStatus: 'Select Code Status Preference',
    editCodeStatus: 'Edit Code Status',
    codeStatusHeader: 'Code Status: {status}',
    files: 'Files:',
    edit: 'Edit',
    notes: 'Notes:',
});

export type CodeStatusSectionOwnProps = { profileId: string };

export type CodeStatusSectionProps = CodeStatusSectionOwnProps;

const CodeStatusSection: React.FC<CodeStatusSectionProps> = (props) => {
    const editCodeStatusSideDrawer = useMaybeRenderedSideDrawerToggle({
        contentKey: 'edit-code-status',
        title: intl('editCodeStatus'),
        renderContent: (onClose) => (
            <EditCodeStatusSideDrawerContent medicalProfileId={props.profileId} onClose={onClose} />
        ),
        mobileOnFocus: () => navigate('edit-code-status', { profileId: props.profileId }),
    });
    const canEditCodeStatus = useHasPermission(props.profileId, 'idsAndInfo', 'edit');
    const { isLoadingCodeStatus, codeStatus } = useMedicalProfileDirectives({
        medicalProfileId: props.profileId,
    });
    const codeStatusDescriptiveLabel = useCodeStatusDescriptiveLabel(codeStatus?.codeStatus || null);

    const { navigate } = useProfilesTabNavigation();

    const renderNoCodeStatus = () => {
        if (!canEditCodeStatus) return null;

        return (
            <Button
                size="lg"
                title={intl('selectCodeStatus')}
                onPress={editCodeStatusSideDrawer.onOpen}
                testID="add-code-status-button"
            />
        );
    };

    const renderCodeStatusContent = (codeStatus: APITypes.medicalProfileService.ProfileCodeStatusResponse) => {
        return (
            <Stack vertical space={contentStackSpacing} align="flex-start">
                <Stack vertical>
                    <Text variant="bold-h5" testID="selectedCodeStatusHeader">
                        {intl('codeStatusHeader', { status: codeStatusTypeMessages(codeStatus.codeStatus) })}
                    </Text>
                    {codeStatusDescriptiveLabel}
                </Stack>

                {codeStatus.codeStatusNotes ? (
                    <Stack vertical>
                        <Text variant="bold-h5">{intl('notes')}</Text>
                        <Text variant="body-1">{codeStatus.codeStatusNotes}</Text>
                    </Stack>
                ) : null}
                {codeStatus?.medicalProfileFiles?.length > 0 ? (
                    <Stack vertical space={1} align="flex-start" testID="codeStatusFiles">
                        <Text variant="bold-body-1">{intl('files')}</Text>
                        {codeStatus.medicalProfileFiles.map((file) => (
                            <FileCard
                                key={file.medicalProfileFileId}
                                file={file}
                                onPress={() => {
                                    navigate('file', { fileId: file.medicalProfileFileId, profileId: props.profileId });
                                }}
                            />
                        ))}
                    </Stack>
                ) : null}
                {canEditCodeStatus ? (
                    <Button
                        size="sm"
                        title={intl('edit')}
                        onPress={editCodeStatusSideDrawer.onOpen}
                        icon="Edit2"
                        testID="edit-code-status-button"
                    />
                ) : null}
                <Divider horizontal />
            </Stack>
        );
    };

    return (
        <>
            <View style={{ alignSelf: 'stretch' }}>
                <AnimateLoadedContent isLoading={isLoadingCodeStatus} style={{ height: 'auto' }}>
                    {() => (codeStatus ? renderCodeStatusContent(codeStatus) : renderNoCodeStatus())}
                </AnimateLoadedContent>
            </View>
            {editCodeStatusSideDrawer.SideDrawer}
        </>
    );
};

export default React.memo(CodeStatusSection);
